import React, { createContext, useContext, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Card, Button } from './UnifiedUIComponents';
import {
  FaTimes,
  FaExclamationTriangle,
  FaCheckCircle,
  FaInfoCircle,
  FaArrowLeft,
  FaWindowMinimize,
  FaWindowMaximize,
  FaWindowRestore
} from 'react-icons/fa';

/**
 * Reusable Modal Components
 * Used across all pages for consistency
 */

const WindowButton = ({ onClick, title, variant, children }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    aria-label={title}
    className={`modal-window-btn${variant ? ` modal-window-btn-${variant}` : ''}`}
  >
    {children}
  </button>
);

const getTitleText = (node) => {
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(getTitleText).join(' ');
  if (React.isValidElement(node)) return getTitleText(node.props.children);
  return '';
};

// Only a size the person dragged the window to is remembered, per modal.
const readSavedSize = (key) => {
  try {
    const saved = JSON.parse(localStorage.getItem(key) || 'null');
    return saved && Number.isFinite(saved.width) && Number.isFinite(saved.height)
      ? { width: saved.width, height: saved.height }
      : null;
  } catch (error) {
    return null;
  }
};

const saveSize = (key, size) => {
  try {
    localStorage.setItem(key, JSON.stringify(size));
  } catch (error) {
    // Storage unavailable — the window simply opens at its default size next time.
  }
};

// Lightweight cross-instance registry so multiple simultaneously-minimized
// modals dock side by side instead of stacking on top of each other. Plain
// module state (not React state) since instances don't share a common
// ancestor to hold this in — every mounted Modal subscribes to `notify()`
// so all of them re-render and recompute their slot whenever the set changes.
let minimizedOrder = [];
const minimizedSubscribers = new Set();
const notifyMinimizedChange = () => minimizedSubscribers.forEach((fn) => fn());

const useMinimizedSlot = (active, id) => {
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    const listener = () => forceUpdate((n) => n + 1);
    minimizedSubscribers.add(listener);
    return () => minimizedSubscribers.delete(listener);
  }, []);

  useEffect(() => {
    const isRegistered = minimizedOrder.includes(id);
    if (active && !isRegistered) {
      minimizedOrder = [...minimizedOrder, id];
      notifyMinimizedChange();
    } else if (!active && isRegistered) {
      minimizedOrder = minimizedOrder.filter((x) => x !== id);
      notifyMinimizedChange();
    }
    return () => {
      if (minimizedOrder.includes(id)) {
        minimizedOrder = minimizedOrder.filter((x) => x !== id);
        notifyMinimizedChange();
      }
    };
  }, [active, id]);

  return minimizedOrder.indexOf(id);
};

// Slots a Modal exposes to its children, so a form that lives deep inside the
// body can still put its buttons in the pinned footer (or a toolbar under the
// title) without the caller having to hoist that JSX into a prop.
const ModalSlotContext = createContext({ footer: null, subheader: null });

// Pinned action row. Use inside a <Modal> in place of the `footer` prop.
export const ModalFooter = ({ children }) => {
  const { footer } = useContext(ModalSlotContext);
  return footer ? createPortal(children, footer) : null;
};

// Pinned strip directly under the title bar — record context and view actions.
export const ModalSubheader = ({ children }) => {
  const { subheader } = useContext(ModalSlotContext);
  return subheader ? createPortal(children, subheader) : null;
};

/**
 * The one modal window used across the app. Every modal — view, data entry,
 * confirm — shares this chrome (`.modal-*` classes in App.css): title bar you
 * can drag, a scrolling body, a pinned footer, and minimize/maximize/resize.
 *
 * size: 'sm' 420px · 'md' 560px · 'lg' 820px · 'xl' 1120px. Pick the smallest
 * that fits the content; all of them clamp to the viewport on a tablet.
 */
export const Modal = ({
  isOpen,
  onClose,
  onBack = null,
  title,
  children,
  footer = null,
  storageKey = null,
  size = 'md',
  closeButton = true,
  resizable = true,
  maximizable = true,
  minimizable = true,
  closeOnBackdropClick = true,
  onKeyDown = null,
  zIndex = undefined
}) => {
  const modalStorageKey = storageKey || `primeclean:modal-size:${window.location.pathname}:${getTitleText(title).trim().toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  const [isMaximized, setIsMaximized] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [customSize, setCustomSize] = useState(() => readSavedSize(modalStorageKey));
  const [windowPosition, setWindowPosition] = useState({ x: 0, y: 0 });
  const [footerSlot, setFooterSlot] = useState(null);
  const [subheaderSlot, setSubheaderSlot] = useState(null);
  const slots = useMemo(() => ({ footer: footerSlot, subheader: subheaderSlot }), [footerSlot, subheaderSlot]);
  const wrapperRef = useRef(null);
  const instanceId = useId();
  const minimizedSlot = useMinimizedSlot(isOpen && isMinimized, instanceId);

  // Every open starts centred and un-maximized; only a size the person
  // dragged to is carried over.
  useEffect(() => {
    if (!isOpen) {
      setIsMaximized(false);
      setIsMinimized(false);
      setWindowPosition({ x: 0, y: 0 });
    } else {
      setCustomSize(readSavedSize(modalStorageKey));
    }
  }, [isOpen, modalStorageKey]);

  if (!isOpen) return null;

  const startResize = (e) => {
    e.preventDefault();
    const rect = wrapperRef.current?.getBoundingClientRect();
    if (!rect) return;
    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = rect.width;
    const startHeight = rect.height;
    let resized = null;

    const onMouseMove = (moveEvent) => {
      resized = {
        width: Math.min(window.innerWidth - 32, Math.max(360, startWidth + (moveEvent.clientX - startX))),
        height: Math.min(window.innerHeight - 32, Math.max(200, startHeight + (moveEvent.clientY - startY)))
      };
      setCustomSize(resized);
    };
    const onMouseUp = () => {
      if (resized) saveSize(modalStorageKey, resized);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const startMove = (e) => {
    if (isMaximized || e.button !== 0 || e.target.closest('button, input, select, textarea, a')) return;
    e.preventDefault();
    const rect = wrapperRef.current?.getBoundingClientRect();
    if (!rect) return;
    const startX = e.clientX;
    const startY = e.clientY;
    const initialPosition = windowPosition;
    // Keep the title bar reachable: it can never leave the top of the
    // screen, and at least 120px of it stays on-screen sideways.
    const onMouseMove = (moveEvent) => {
      const dx = Math.min(window.innerWidth - 120 - rect.left, Math.max(120 - rect.right, moveEvent.clientX - startX));
      const dy = Math.min(window.innerHeight - 48 - rect.top, Math.max(-rect.top, moveEvent.clientY - startY));
      setWindowPosition({ x: initialPosition.x + dx, y: initialPosition.y + dy });
    };
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const windowStyle = isMaximized
    ? undefined
    : {
        transform: `translate(${windowPosition.x}px, ${windowPosition.y}px)`,
        ...(customSize ? { width: `${customSize.width}px`, height: `${customSize.height}px`, maxWidth: 'calc(100vw - 32px)' } : {})
      };

  return (
    <>
      {isMinimized && (
        <div
          className="modal-minimized-tab"
          style={{ left: `${16 + Math.max(0, minimizedSlot) * 240}px` }}
          onClick={() => setIsMinimized(false)}
        >
          <span className="modal-minimized-tab-title">{title}</span>
          <WindowButton
            variant="maximize"
            title="Restore"
            onClick={(e) => { e.stopPropagation(); setIsMinimized(false); }}
          >
            <FaWindowRestore />
          </WindowButton>
          {closeButton && (
            <WindowButton
              variant="close"
              title="Close"
              onClick={(e) => { e.stopPropagation(); onClose && onClose(); }}
            >
              <FaTimes />
            </WindowButton>
          )}
        </div>
      )}
      {/* Kept mounted (just hidden) rather than unmounted while minimized —
          unmounting would reset every field, dropdown, and scroll position
          in whatever form the caller put in `children`. */}
      <div
        className="modal-overlay"
        role="dialog"
        aria-modal="true"
        onMouseDown={closeOnBackdropClick ? (e) => { if (e.target === e.currentTarget) onClose && onClose(); } : undefined}
        onKeyDown={onKeyDown || undefined}
        style={{ ...(isMinimized ? { display: 'none' } : null), ...(zIndex ? { zIndex } : null) }}
      >
        <div
          ref={wrapperRef}
          className={`modal modal-${size}${isMaximized ? ' modal-maximized' : ''}`}
          style={windowStyle}
        >
          <div className="modal-header modal-drag-handle" onMouseDown={startMove} onDoubleClick={() => setWindowPosition({ x: 0, y: 0 })}>
            <div className="modal-header-main">
              {onBack && (
                <WindowButton title="Back" onClick={onBack}>
                  <FaArrowLeft />
                </WindowButton>
              )}
              <h2 className="modal-title">{title}</h2>
            </div>
            <div className="modal-window-controls">
              {minimizable && (
                <WindowButton title="Minimize" onClick={() => setIsMinimized(true)}>
                  <FaWindowMinimize />
                </WindowButton>
              )}
              {maximizable && (
                <WindowButton
                  title={isMaximized ? 'Restore' : 'Maximize'}
                  onClick={() => setIsMaximized((prev) => !prev)}
                >
                  {isMaximized ? <FaWindowRestore /> : <FaWindowMaximize />}
                </WindowButton>
              )}
              {closeButton && (
                <WindowButton variant="close" title="Close" onClick={onClose}>
                  <FaTimes />
                </WindowButton>
              )}
            </div>
          </div>

          <div className="modal-subheader" ref={setSubheaderSlot} />

          <div className="modal-body app-modal-body">
            <ModalSlotContext.Provider value={slots}>
              {children}
            </ModalSlotContext.Provider>
          </div>

          {footer && <div className="modal-footer">{footer}</div>}
          <div className="modal-footer" ref={setFooterSlot} />

          {resizable && !isMaximized && (
            <div
              className="modal-resize-handle"
              onMouseDown={startResize}
              title="Drag to resize"
            />
          )}
        </div>
      </div>
    </>
  );
};

/**
 * useModalChrome — adds minimize/maximize/resize behavior to the app's
 * plain CSS-class modals (`.modal-overlay` / `.modal` / `.modal-header`),
 * mirroring what `Modal` above already does for its own markup, without
 * requiring those call sites to be rewritten around `Modal`'s children/
 * footer props. Spread `modalStyle` into the `.modal` div's existing style
 * (after any of its own overrides, so maximize/resize win), spread
 * `overlayStyle` into the `.modal-overlay` div's, attach `modalRef` to the
 * `.modal` div, and render `ModalChromeButtons` / `ModalChromeResizeHandle`
 * / `ModalMinimizedTab` at the spots documented on each below.
 */
export const useModalChrome = (isOpen, { resizable = true, maximizable = true, minimizable = true } = {}) => {
  const [isMaximized, setIsMaximized] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [customSize, setCustomSize] = useState(null);
  const modalRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      setIsMaximized(false);
      setIsMinimized(false);
      setCustomSize(null);
    }
  }, [isOpen]);

  const startResize = (e) => {
    e.preventDefault();
    const rect = modalRef.current?.getBoundingClientRect();
    if (!rect) return;
    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = rect.width;
    const startHeight = rect.height;
    const onMouseMove = (moveEvent) => {
      setCustomSize({
        width: Math.max(360, startWidth + (moveEvent.clientX - startX)),
        height: Math.max(240, startHeight + (moveEvent.clientY - startY))
      });
    };
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const modalStyle = isMaximized
    ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, width: '100vw', height: '100vh', maxWidth: 'none', maxHeight: 'none', borderRadius: 0 }
    : customSize
      ? { width: `${customSize.width}px`, maxWidth: 'none', height: `${customSize.height}px`, maxHeight: 'none' }
      : {};

  return {
    modalRef,
    isMaximized,
    isMinimized,
    resizable,
    maximizable,
    minimizable,
    toggleMaximize: () => setIsMaximized((prev) => !prev),
    minimize: () => setIsMinimized(true),
    restore: () => setIsMinimized(false),
    startResize,
    modalStyle,
    overlayStyle: isMinimized ? { display: 'none' } : {}
  };
};

// Header buttons — place inside `.modal-header`'s button cluster, before the
// existing `.modal-close` (×) button.
export const ModalChromeButtons = ({ chrome }) => (
  <>
    {chrome.minimizable && (
      <WindowButton title="Minimize" onClick={chrome.minimize}>
        <FaWindowMinimize />
      </WindowButton>
    )}
    {chrome.maximizable && (
      <WindowButton title={chrome.isMaximized ? 'Restore' : 'Maximize'} onClick={chrome.toggleMaximize}>
        {chrome.isMaximized ? <FaWindowRestore /> : <FaWindowMaximize />}
      </WindowButton>
    )}
  </>
);

// Drag handle — place as the last child of the `.modal` div (a sibling
// after `.modal-header`/`.modal-body`/footer), so it anchors bottom-right.
export const ModalChromeResizeHandle = ({ chrome }) => (
  chrome.resizable && !chrome.isMaximized ? (
    <div className="modal-resize-handle" onMouseDown={chrome.startResize} title="Drag to resize" />
  ) : null
);

// Floating dock shown while minimized. Render as a sibling immediately
// outside the `.modal-overlay` div (same `{showX && (<>...)}` fragment) so
// it still renders while the overlay itself is `display:none`.
export const ModalMinimizedTab = ({ chrome, title, onClose }) => {
  const instanceId = useId();
  const slot = useMinimizedSlot(chrome.isMinimized, instanceId);
  if (!chrome.isMinimized) return null;
  return (
    <div
      className="modal-minimized-tab"
      style={{ left: `${16 + Math.max(0, slot) * 240}px` }}
      onClick={chrome.restore}
    >
      <span className="modal-minimized-tab-title">{title}</span>
      <WindowButton variant="maximize" title="Restore" onClick={(e) => { e.stopPropagation(); chrome.restore(); }}>
        <FaWindowRestore />
      </WindowButton>
      {onClose && (
        <WindowButton variant="close" title="Close" onClick={(e) => { e.stopPropagation(); onClose(); }}>
          <FaTimes />
        </WindowButton>
      )}
    </div>
  );
};

/**
 * Confirmation Dialog
 */
export const ConfirmDialog = ({
  isOpen,
  onConfirm,
  onCancel,
  title = 'Confirm Action',
  message = 'Are you sure?',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'warning'
}) => {
  const variantConfig = {
    warning: { icon: FaExclamationTriangle, color: 'var(--warning-color)' },
    danger: { icon: FaExclamationTriangle, color: 'var(--danger-color)' },
    success: { icon: FaCheckCircle, color: 'var(--success-color)' },
    info: { icon: FaInfoCircle, color: 'var(--info-color)' },
  };

  const config = variantConfig[variant] || variantConfig.warning;
  const Icon = config.icon;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      title={title}
      size="sm"
      resizable={false}
      maximizable={false}
      minimizable={false}
      footer={
        <>
          <Button variant="ghost" onClick={onCancel}>
            {cancelText}
          </Button>
          <Button
            variant={variant === 'danger' ? 'danger' : 'primary'}
            onClick={onConfirm}
          >
            {confirmText}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start' }}>
        <div style={{
          fontSize: 'var(--fs-3xl)',
          color: config.color,
          flexShrink: 0
        }}>
          <Icon />
        </div>
        <p style={{
          color: 'var(--text-secondary)',
          fontSize: 'var(--fs-lg)',
          lineHeight: 1.6,
          margin: 0
        }}>
          {message}
        </p>
      </div>
    </Modal>
  );
};

/**
 * Alert/Notification Dialog
 */
export const AlertDialog = ({
  isOpen,
  onClose,
  title = 'Alert',
  message = '',
  type = 'info'
}) => {
  const typeConfig = {
    success: { color: 'var(--success-color)', icon: FaCheckCircle },
    error: { color: 'var(--danger-color)', icon: FaExclamationTriangle },
    warning: { color: 'var(--warning-color)', icon: FaExclamationTriangle },
    info: { color: 'var(--info-color)', icon: FaInfoCircle },
  };

  const config = typeConfig[type] || typeConfig.info;
  const Icon = config.icon;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="sm"
      resizable={false}
      maximizable={false}
      minimizable={false}
      footer={<Button variant="primary" onClick={onClose}>OK</Button>}
    >
      <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
        <div style={{
          fontSize: 'var(--fs-2xl)',
          color: config.color,
          flexShrink: 0
        }}>
          <Icon />
        </div>
        <p style={{
          color: 'var(--text-secondary)',
          margin: 0
        }}>
          {message}
        </p>
      </div>
    </Modal>
  );
};

/**
 * Form Modal
 */
export const FormModal = ({
  isOpen,
  onClose,
  onSubmit,
  title = 'Form',
  size = 'md',
  children,
  isLoading = false
}) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size={size}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={onSubmit}
            loading={isLoading}
          >
            {isLoading ? 'Submitting...' : 'Submit'}
          </Button>
        </>
      }
    >
      {children}
    </Modal>
  );
};

/**
 * Delete Confirmation Modal
 */
export const DeleteConfirmModal = ({
  isOpen,
  onConfirm,
  onCancel,
  itemName = 'item',
  isLoading = false
}) => {
  return (
    <ConfirmDialog
      isOpen={isOpen}
      onConfirm={onConfirm}
      onCancel={onCancel}
      title="Delete Item"
      message={`Are you sure you want to delete "${itemName}"? This action cannot be undone.`}
      confirmText={isLoading ? 'Deleting...' : 'Delete'}
      cancelText="Cancel"
      variant="danger"
    />
  );
};

/**
 * Loading Modal
 */
export const LoadingModal = ({
  isOpen,
  message = 'Loading...'
}) => {
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: isOpen ? 'flex' : 'none',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgba(0, 0, 0, 0.3)',
      zIndex: 50
    }}>
      <Card style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '1rem',
        padding: '2rem'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid var(--border-light)',
          borderTop: '3px solid var(--primary-color)',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <p style={{
          color: 'var(--text-secondary)',
          margin: 0,
          fontSize: 'var(--fs-lg)'
        }}>
          {message}
        </p>
      </Card>
    </div>
  );
};

/**
 * Drawer/Side Modal
 */
export const Drawer = ({
  isOpen,
  onClose,
  title,
  children,
  footer = null,
  position = 'right'
}) => {
  if (!isOpen) return null;

  const isRight = position === 'right';

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 50
    }}>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          animation: 'fadeIn 200ms ease-out'
        }}
      />

      {/* Drawer */}
      <div style={{
        position: 'absolute',
        top: 0,
        [isRight ? 'right' : 'left']: 0,
        bottom: 0,
        width: 'clamp(280px, 90vw, 420px)',
        backgroundColor: 'var(--card-bg)',
        boxShadow: isRight
          ? '-4px 0 15px -3px rgba(0, 0, 0, 0.1)'
          : '4px 0 15px -3px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        flexDirection: 'column',
        animation: isRight ? 'slideInRight 300ms ease-out' : 'slideInLeft 300ms ease-out'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '1.5rem',
          borderBottom: '1px solid var(--border-light)',
          flexShrink: 0
        }}>
          <h2 style={{
            margin: 0,
            fontSize: 'var(--fs-xl)',
            fontWeight: '700',
            color: 'var(--text-primary)'
          }}>
            {title}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: 'var(--fs-2xl)',
              cursor: 'pointer',
              color: 'var(--text-secondary)',
              transition: 'color var(--transition-base)',
              padding: '0'
            }}
            onMouseEnter={(e) => e.target.style.color = 'var(--text-primary)'}
            onMouseLeave={(e) => e.target.style.color = 'var(--text-secondary)'}
          >
            <FaTimes />
          </button>
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '1.5rem'
        }}>
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div style={{
            padding: '1.5rem',
            borderTop: '1px solid var(--border-light)',
            display: 'flex',
            gap: '1rem',
            flexShrink: 0
          }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Electronic Signature Modal — the password + reason/decision confirmation
 * shown before a GxP-significant action (submit, approve, delete, draft
 * save, ...). Was hand-duplicated near-verbatim across ProductMaster,
 * EquipmentTrain, EquipmentMaster and SamplingPlans; each page still owns its
 * own message/placeholder/label text and confirm handler (those branch on
 * page-specific pending-action state), this component only owns the shared
 * modal chrome + form layout so future style changes land in one place.
 */
export const ESignModal = ({
  isOpen,
  title = 'Electronic Signature Required',
  message,
  showDecision = false,
  decisionOptions = [],
  decisionValue,
  onDecisionChange,
  decisionLabel = 'Approval Decision *',
  commentValue,
  onCommentChange,
  commentPlaceholder = 'Reason / Comment',
  commentLabel = 'Reason / Comment *',
  passwordValue,
  onPasswordChange,
  passwordRef,
  note,
  onCancel,
  onConfirm,
  confirmLabel = 'Confirm & Submit',
  confirmDisabled = false,
  maxWidth = '520px',
}) => {
  if (!isOpen) return null;
  return (
    <div className="modal-overlay modal-overlay-top" onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel && onCancel(); }}>
      <div className="modal" style={{ maxWidth }}>
        <div className="modal-header">
          <h2 className="modal-title">{title}</h2>
          <button className="modal-close" onClick={onCancel}>&times;</button>
        </div>
        <div className="modal-body">
          {message && <p className="text-muted" style={{ marginBottom: '12px' }}>{message}</p>}
          <form autoComplete="off" onSubmit={(e) => e.preventDefault()}>
            <input type="text" name="esign_user_dummy" style={{ display: 'none' }} autoComplete="username" />
            {showDecision && (
              <div className="form-group">
                <label className="form-label">{decisionLabel}</label>
                <select className="form-control" value={decisionValue} onChange={(e) => onDecisionChange(e.target.value)}>
                  {decisionOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="form-group">
              <label className="form-label">{commentLabel}</label>
              <textarea
                className="form-control"
                rows={3}
                value={commentValue}
                onChange={(e) => onCommentChange(e.target.value)}
                placeholder={commentPlaceholder}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Password *</label>
              <input
                ref={passwordRef}
                type="password"
                className="form-control"
                value={passwordValue}
                autoComplete="new-password"
                name="esign_password_field"
                data-lpignore="true"
                data-1p-ignore
                data-bitwarden-ignore
                placeholder="Enter your login password"
                onChange={(e) => onPasswordChange(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && onConfirm) onConfirm(); }}
              />
            </div>
          </form>
          {note}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary" onClick={onConfirm} disabled={confirmDisabled}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Modal;
